<template>
	<popup
		ref="popup"
		class="payment-popup-holder"
		:class="{
			'payment-popup-holder--dark': darkTheme,
			'payment-popup-holder--loading': loading
		}"
	>
		<span v-if="isApplePaySelected" class="card-holder" slot="opener">
			<img :src="applePayIcon" alt="" class="card-holder__image" />
			Apple Pay
		</span>
		<span v-else-if="isGooglePaySelected" class="card-holder" slot="opener">
			<img :src="googlePayIcon" alt="" class="card-holder__image" />
			Google Pay
		</span>
		<span v-else class="card-holder" slot="opener">
			<span class="card-image" :class="creditCardType" />
			<span class="card-number">*{{ creditCardNumbers }}</span>
		</span>

		<template slot="content">
			<div class="payment-popup-title-wrapper">
				<span class="payment-popup-title">Payment Method</span>
				<close-btn :color="darkTheme && '#fff'" @close="hide" />
			</div>

			<div
				class="payment-popup-card"
				:class="{ active: isCardSelected }"
				@click="changePaymentMethod($options.PAYMENT_GATEWAY.CREDIT_CARD)"
			>
				<span class="card-holder card-holder--image">
					<span class="card-image" :class="creditCardType" />
					<span>****{{ creditCardNumbers }}</span>
				</span>
			</div>
			<div
				v-if="isIOSOrMAC || isApplePaySelected"
				class="payment-popup-card"
				:class="{ active: isApplePaySelected }"
				@click="changePaymentMethod($options.PAYMENT_GATEWAY.APPLE_PAY_CARD)"
			>
				<span class="card-holder">
					<img :src="applePayIcon" alt="" class="card-holder__image" />
					Apple Pay
				</span>
			</div>
			<div
				v-if="isAndroid || isGooglePaySelected"
				class="payment-popup-card"
				:class="{ active: isGooglePaySelected }"
				@click="changePaymentMethod($options.PAYMENT_GATEWAY.ANDROID_PAY_CARD)"
			>
				<span class="card-holder">
					<img :src="googlePayIcon" alt="" class="card-holder__image" />
					Google Pay
				</span>
			</div>
		</template>
	</popup>
</template>

<script>
import { mapActions } from 'vuex'
import Popup from '@/components/modals/Popup'
import CloseBtn from '@/components/CloseBtn.vue'
import AppleIcon from '@/assets/images/ic_apple_pay.svg'
import GooglePayIcon from '@/assets/images/ic_google_pay.svg'
import { PAYMENT_GATEWAY } from '@/constants'
import {
	buildTicketingFakeCreditCard,
	TEST_TICKETING_CREDIT_CARD
} from '@/constantsTicketingFakePayment'
import { checkIOs, checkMacOS } from '@/utils/helpers'

export default {
	name: 'PaymentMethodPopup',

	components: {
		CloseBtn,
		Popup
	},

	props: {
		creditCard: {
			type: Object,
			default: () => ({})
		},

		darkTheme: {
			type: Boolean,
			default: false
		}
	},

	PAYMENT_GATEWAY,

	data: () => ({
		isIOSOrMAC: false,
		isAndroid: false,
		loading: false
	}),

	computed: {
		paymentType() {
			return (
				this.creditCard?.cardHolderName ||
				this.creditCard?.paymentGatewayType ||
				this.$options.PAYMENT_GATEWAY.CREDIT_CARD
			)
		},

		isGooglePaySelected() {
			return this.paymentType === this.$options.PAYMENT_GATEWAY.ANDROID_PAY_CARD
		},

		isApplePaySelected() {
			return this.paymentType === this.$options.PAYMENT_GATEWAY.APPLE_PAY_CARD
		},

		isCardSelected() {
			return !this.isApplePaySelected && !this.isGooglePaySelected
		},

		creditCardNumbers() {
			return this.creditCard?.lastFour || TEST_TICKETING_CREDIT_CARD.lastFour
		},

		creditCardType() {
			return (
				this.creditCard?.cardType?.toLowerCase()?.replace(/[/\s]/g, '') ||
				'visa'
			)
		},

		applePayIcon() {
			return AppleIcon
		},

		googlePayIcon() {
			return GooglePayIcon
		}
	},

	mounted() {
		if (typeof navigator === 'undefined') return

		this.isIOSOrMAC = checkIOs() || checkMacOS()
		this.isAndroid = !this.isIOSOrMAC
	},

	methods: {
		...mapActions('payment', ['addCreditCard']),
		...mapActions('cart', ['updateCart']),

		hide() {
			if (this.$refs.popup) this.$refs.popup.hide()
		},

		async changePaymentMethod(paymentMethod) {
			if (this.loading) return

			try {
				this.loading = true
				const creditCard = await this.addCreditCard(
					buildTicketingFakeCreditCard(paymentMethod)
				)

				if (creditCard?.id) {
					await this.updateCart({ creditCardId: creditCard.id })
				}
			} catch (e) {
			} finally {
				this.loading = false
				this.hide()
			}
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.payment-popup-holder {
	color: $grey-dark;

	.popup {
		padding-bottom: 2px;
		font-size: 16px;
	}

	.popup-opener {
		background-color: $background-popup;
		border: 1px solid $border-color;
		border-radius: 4px;
		margin-bottom: 15px;

		.card-holder {
			&:not(.card-holder--image) {
				padding-left: 15px;
			}
		}
	}

	.popup-opener:after {
		top: 16px;
	}

	&.opened .popup-opener:after {
		top: 18px;
	}

	&--loading {
		pointer-events: none;
	}

	.close-popup-btn {
		top: 8px;
	}

	&--dark {
		.card-number,
		.payment-popup-title,
		.payment-popup-card,
		.card-holder {
			color: $white;
		}

		.popup-opener {
			&:after {
				border-color: $semi-dark-blue;
			}
		}

		.popup {
			background: $background-popup;
			border: 1px solid $border-color;
		}

		.payment-popup-card {
			color: $white;
			background: transparent;

			&.active {
				background: rgba($blue-faceless-void, 0.25);
			}
		}
	}
}

.card-holder {
	display: flex;
	align-items: center;
	min-height: 28px;

	&__image {
		margin-right: 10px;
		max-width: 35px;
	}

	.card-image {
		display: inline-block;
		width: 44px;
		height: 28px;
		background-image: url('https://s3-us-west-2.amazonaws.com/s.cdpn.io/346994/card_sprite.png');
		background-size: 86px 458px;
		border-radius: 3px;
		background-position: -100px 0;
		background-repeat: no-repeat;

		&.visa {
			margin-left: -3px;
			background-position: 0 -398px;
		}

		&.mastercard,
		&.mc {
			background-position: 0 -281px;
		}

		&.americanexpress,
		&.amex {
			background-position: 0 -370px;
		}

		&.discover,
		&.dcvr {
			background-image: url('~@/assets/images/ic_discover.jpg');
			background-position: 0 0;
			background-size: 44px 28px;
			margin-right: 5px;
		}

		&.maestro {
			background-position: 0 -251px;
		}

		&.jcb {
			background-position: 0 -221px;
		}

		&.dinersclub {
			background-position: 0 -133px;
		}
	}
}

.payment-popup-title-wrapper {
	position: relative;
	padding: 0 16px;
}

.payment-popup-title {
	display: block;
	padding: 0 0 10px;
	font-size: 20px;
	text-align: center;
	color: $grey;
}

.payment-popup-card {
	margin: 0 -16px;
	padding: 10px 16px;
	background: transparent;
	position: relative;
	cursor: pointer;

	&.active:after {
		position: absolute;
		right: 17px;
		top: 17px;
		width: 13px;
		height: 6px;
		border: solid $semi-dark-blue;
		border-width: 0 0 2px 2px;
		transform: rotate(-45deg);
		content: '';
	}
}

@media (max-width: 767px) {
	.payment-popup-holder {
		.popup {
			width: calc(100vw - 24px);
			bottom: 100%;
			margin: 0 0 10px;
		}
	}
}
</style>
